import classNames from 'classnames'
import { useApp } from '../../../hooks/useApp'
import { getCorrespondingPendantProductVariantIdsByMainProductVariantId } from '../../../utils'
import { updateUrlQueryProductVariantIds } from '../../../context/AppContext/utils'
import { MainProductVariant } from '../../../types'
import { formatYGWG } from '../../../utils/formatCentsToDollars'
import { Text } from '../../ui/Text/Text'

interface Props {
  className?: string
  showLabel?: boolean
}

export const MetalToggler = ({ className, showLabel }: Props) => {
  const {
    mainProduct,
    linkedMainProducts,
    activeMainProductVariantId,
    setActiveMainProductVariantId,
    setConfirmationPopupDisplayed,
    activePendantProductVariantIds,
    setRedirectingToNextPage,
    setBufferedMainProduct,
    setBufferedMainProductVariant,
    isMainProductSinglePendant,
    isCarabinerProductMode
  } = useApp()
  const shouldShowLabel = showLabel ?? (isMainProductSinglePendant || isCarabinerProductMode)

  const allColors = [mainProduct, ...linkedMainProducts]
    .map((product) => {
      const metalOption = product.optionsWithValues.find((option) => option.name.toLowerCase() === 'metal')

      if (!metalOption) {
        throw new Error(`No metal option found for product ${product.title}. Ensure one metal option exists per product.`)
      }

      if (metalOption.values.length > 1) {
        throw new Error(`Multiple metal options found for product ${product.title}. Ensure only one metal option exists per product.`)
      }

      return metalOption.values[0]
    })
    .sort((a, b) => b.localeCompare(a))

  const activeColor = mainProduct.optionsWithValues.find((option) => option.name.toLowerCase() === 'metal')?.values[0]

  if (allColors.length === 0) {
    console.warn('MetalToggler requires at least 1 product with a metal option to function properly. Check the product configuration.')
    return null
  }

  const changeColor = () => {
    const currentVariant = mainProduct.variants.find((variant) => +variant.id === +activeMainProductVariantId)!

    let targetVariant: MainProductVariant | undefined

    for (const product of linkedMainProducts) {
      const productMetalIndex = product.optionsWithValues.findIndex((optionsWithValues) => optionsWithValues.name.toLowerCase() === 'metal')
      if (productMetalIndex === -1) continue

      const foundVariant = product.variants.find((variant) => {
        return variant.options.every((option, index) => {
          if (index === productMetalIndex) return option !== currentVariant.options[index]
          return option === currentVariant.options[index]
        })
      })

      if (foundVariant) {
        targetVariant = foundVariant
        break
      }
    }

    if (!targetVariant) {
      console.error('No variant found with different metal')
      setActiveMainProductVariantId(mainProduct.variants[0].id)
      return
    }

    setActiveMainProductVariantId(targetVariant.id)

    const productVariant = targetVariant
    const id = productVariant.id

    const linkedBuilderProduct = linkedMainProducts.find((linkedBuilderProduct) => linkedBuilderProduct.variants.some((variant) => variant.id === id))

    if (!linkedBuilderProduct) return

    setRedirectingToNextPage(true)

    const allPendantsAvailable = linkedBuilderProduct.metafields.pendantProducts
      .flatMap((pendantProduct) => pendantProduct.id)
      .every((pendantProductId) => mainProduct.metafields.pendantProducts.flatMap((pendantProduct) => pendantProduct.id).includes(pendantProductId))

    if (allPendantsAvailable) {
      const correspondingPendantProductVariantIds = getCorrespondingPendantProductVariantIdsByMainProductVariantId(
        linkedBuilderProduct,
        id,
        activePendantProductVariantIds
      )
      const url = new URL(window.location.href)
      url.pathname = linkedBuilderProduct.url
      window.history.replaceState({}, '', url.href)

      updateUrlQueryProductVariantIds(
        linkedBuilderProduct,
        String(productVariant.id),
        isCarabinerProductMode ? [] : correspondingPendantProductVariantIds
      )

      window.reloadBuilder?.(linkedBuilderProduct)
      return
    }

    setBufferedMainProduct(linkedBuilderProduct)
    setBufferedMainProductVariant(productVariant)
    setConfirmationPopupDisplayed(true)
  }

  return (
    <div className={classNames("colors-swatch-wrapper", className)}>
      {shouldShowLabel && <span className="colors-swatch__label">Material: </span>}
      <ul className="colors-swatch__list">
        {allColors.map((color, index) => {
          const formattedColor = color === 'YG' || color === 'WG' ? formatYGWG(color) : color

          const handleClick = () => {
            if (color === activeColor) return
            changeColor()
          }

          return (
            <li key={index} className={classNames('colors-swatch__item', color === activeColor && 'colors-swatch__item--active')}>
              <button key={index} onClick={handleClick}>
                <Text className="uppercase">{formattedColor}</Text>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
